import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import LoadingCard from './LoadingCard';

import { CustomerSelect } from '@/components/CustomerSelect';
import { FormGroup } from '@/components/FormGroup';
import { PasswordInput } from '@/components/PasswordInput';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { ROUTE_PATHS } from '@/config/constants';
import { useGetUserById } from '@/hooks/useGetUserById';
import { useUpdateUser } from '@/hooks/useUpdateUser';
import {
  RoleEnum,
  StatusEnum,
  UpdateUserDto,
  User,
} from '@/services/UserService';

const userEditSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .optional()
    .or(z.literal('')),
  role: z.nativeEnum(RoleEnum),
  status: z.nativeEnum(StatusEnum),
  customer: z.string().min(1, 'Customer is required'),
});

type UserEditFormData = z.infer<typeof userEditSchema>;

interface UserProps {
  id: string | undefined;
  onSuccess: (value: boolean) => void;
}

export default function EditCustomersForm({ id, onSuccess }: UserProps) {
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  const { user } = useGetUserById(Number(id));

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<UserEditFormData>({
    resolver: zodResolver(userEditSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: RoleEnum.User,
      status: StatusEnum.Active,
      customer: '',
    },
  });

  const handleSetPassword = useCallback(
    (value: string) => {
      setValue('password', value);
    },
    [setValue],
  );

  useEffect(() => {
    if (!user) return;

    const fetchUser = async () => {
      try {
        reset({
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          password: '',
          role: user.role?.id ?? RoleEnum.User,
          status: user.status?.id ?? StatusEnum.Active,
          customer: user.customer?.id?.toString() || '',
        });
        setIsLoading(false);
      } catch (error) {
        toast.error('Failed to load user data. Please try again.');
        navigate(ROUTE_PATHS.ADMIN_USERS);
      }
    };

    fetchUser();
  }, [reset, user, navigate]);

  function getUpdatedFields(
    original: User,
    updated: UserEditFormData,
  ): Partial<UpdateUserDto> {
    const changes: Partial<UpdateUserDto> = {};

    if (original.firstName !== updated.firstName) {
      changes.firstName = updated.firstName;
    }
    if (original.lastName !== updated.lastName) {
      changes.lastName = updated.lastName;
    }
    if (original.email !== updated.email) {
      changes.email = updated.email;
    }
    if (updated.password && updated.password.length >= 8) {
      changes.password = updated.password;
    }
    if (original.role.id !== updated.role) {
      changes.role = { id: updated.role };
    }
    if (original.status.id !== updated.status) {
      changes.status = { id: updated.status };
    }

    if (original.customer.id) {
      if (original.customer.id.toString() !== updated.customer) {
        changes.customer = { id: Number(updated.customer) };
      }
    }

    return changes;
  }

  const { updateUser } = useUpdateUser(Number(id));

  const onSubmit = handleSubmit(async (data: UserEditFormData) => {
    try {
      if (user) {
        const dataUpdated = getUpdatedFields(user, data);
        if (Object.keys(dataUpdated).length === 0) {
          toast.info('No changes to update.');
          return;
        }
        await updateUser(dataUpdated);
      }
      onSuccess(true);
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 422) {
        toast.error('Email already exists. Please try again.');
        return;
      }
      toast.error('Failed to update user. Please try again.');
    }
  });

  if (isLoading) {
    return <LoadingCard />;
  }

  return (
    <Card className="mx-auto w-full max-w-2xl">
      <CardHeader>
        <CardTitle>Edit User</CardTitle>
      </CardHeader>

      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <FormGroup error={{ message: errors.firstName?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="firstName">First Name</RequiredLabel>
            <Input id="firstName" {...register('firstName')} />
          </FormGroup>

          <FormGroup error={{ message: errors.lastName?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="lastName">Last Name</RequiredLabel>
            <Input id="lastName" {...register('lastName')} />
          </FormGroup>

          <FormGroup error={{ message: errors.email?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="email">Email</RequiredLabel>
            <Input id="email" type="email" {...register('email')} />
          </FormGroup>

          <FormGroup error={{ message: errors.password?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="password">
              Password (leave blank to keep current)
            </RequiredLabel>

            <PasswordInput
              id="password"
              {...register('password')}
              generatorButton={{
                enable: true,
                onGenerate: handleSetPassword,
              }}
            />
          </FormGroup>

          <FormGroup error={{ message: errors.role?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="role">Role</RequiredLabel>
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <Select
                  onValueChange={(value) => field.onChange(Number(value))}
                  defaultValue={
                    field.value?.toString() || RoleEnum.User.toString()
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={RoleEnum.User.toString()}>
                      User
                    </SelectItem>
                    <SelectItem value={RoleEnum.Admin.toString()}>
                      Admin
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </FormGroup>

          <FormGroup error={{ message: errors.status?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="status">Status</RequiredLabel>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Select
                  onValueChange={(value) => field.onChange(Number(value))}
                  defaultValue={
                    field.value?.toString() || StatusEnum.Active.toString()
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={StatusEnum.Active.toString()}>
                      Active
                    </SelectItem>
                    <SelectItem value={StatusEnum.Inactive.toString()}>
                      Inactive
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </FormGroup>

          <FormGroup error={{ message: errors.customer?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="customer">Customer</RequiredLabel>
            <Controller
              name="customer"
              control={control}
              render={({ field }) => (
                <CustomerSelect
                  onValueChange={field.onChange}
                  defaultValue={field.value?.toString() || ''}
                />
              )}
            />
          </FormGroup>

          <p className="mt-4 text-sm text-gray-500">
            <span className="text-red-500">*</span> Required field
          </p>
          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(ROUTE_PATHS.ADMIN_USERS)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={isSubmitting || isLoading}
            >
              {(isSubmitting || isLoading) && (
                <Loader2 className="animate-spin" />
              )}

              {!isSubmitting && !isLoading && 'Update User'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
